import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowRight, Certificate as CertIcon, LockKey } from "@phosphor-icons/react";

// Motion variants — hoisted out of the component so React/Framer treats them
// as referentially stable (no re-mount on parent re-render).
const HERO_INITIAL = { opacity: 0, y: 12 };
const HERO_ANIMATE = { opacity: 1, y: 0 };
const HERO_TRANSITION = { duration: 0.5 };
const ASIDE_INITIAL = { opacity: 0, scale: 0.97 };
const ASIDE_ANIMATE = { opacity: 1, scale: 1 };
const ASIDE_TRANSITION = { duration: 0.6, delay: 0.1 };

export default function VerifySearch() {
  const navigate = useNavigate();
  const [certNo, setCertNo] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    const trimmed = certNo.trim();
    if (!trimmed) return;
    navigate(`/verify/${encodeURIComponent(trimmed)}`);
  };

  return (
    <div className="min-h-screen grain-bg flex flex-col">
      <header className="border-b border-[var(--vs-border)] bg-[var(--vs-bg)]/85 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center font-display font-bold">VS</div>
            <div className="leading-tight">
              <div className="font-display font-semibold">Vidya Seva</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--vs-text-secondary)]">Public verification</div>
            </div>
          </Link>
          <Link to="/login" className="text-xs underline underline-offset-4 hover:text-[var(--vs-secondary)]">Staff sign-in →</Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={HERO_INITIAL} animate={HERO_ANIMATE} transition={HERO_TRANSITION}
            className="lg:col-span-7"
          >
            <div className="eyebrow mb-3">Public certificate registry</div>
            <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              Verify a certificate issued by{" "}
              <span className="italic font-light text-[var(--vs-secondary)]">Vidya Seva Welfare Trust</span>.
            </h1>
            <p className="mt-4 text-sm lg:text-base text-[var(--vs-text-secondary)] max-w-2xl leading-relaxed">
              Paste the certificate number printed on the document (for example{" "}
              <span className="font-mono text-[var(--vs-text)]">VS/TC/2026/0001</span>) or scan the QR on the certificate.
              No login is required — anyone can verify.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-3" data-testid="verify-search-form">
              <label className="block text-xs font-mono tracking-widest uppercase text-[var(--vs-secondary)]">Certificate number</label>
              <div className="flex gap-3">
                <input
                  type="text"
                  required
                  value={certNo}
                  onChange={(e) => setCertNo(e.target.value)}
                  data-testid="verify-search-input"
                  className="flex-1 px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white font-mono text-sm focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
                  placeholder="VS/TC/2026/0001"
                />
                <button
                  type="submit"
                  data-testid="verify-search-submit"
                  className="px-6 py-3 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] hover:bg-[var(--vs-secondary-hover)] transition-all inline-flex items-center gap-2 font-medium"
                >
                  Verify <ArrowRight size={16} />
                </button>
              </div>
              <p className="text-[11px] text-[var(--vs-text-secondary)] mt-1">Tip: the QR code on every certificate links directly to its verification page.</p>
            </form>

            <div className="mt-10 flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
                <LockKey size={14} weight="fill" /> AES-256 registry
              </div>
              <div className="w-px h-4 bg-[var(--vs-border)]" />
              <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
                <ShieldCheck size={14} weight="fill" /> TLS 1.2+
              </div>
              <div className="w-px h-4 bg-[var(--vs-border)]" />
              <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
                <CertIcon size={14} weight="fill" /> Public · No login
              </div>
            </div>
          </motion.div>

          <motion.aside
            initial={ASIDE_INITIAL} animate={ASIDE_ANIMATE} transition={ASIDE_TRANSITION}
            className="lg:col-span-5"
          >
            <div className="vs-card p-6">
              <div className="eyebrow mb-3">For verifying institutions</div>
              <ol className="space-y-3 text-sm text-[var(--vs-text-secondary)]">
                <li><span className="font-display text-[var(--vs-text)] font-semibold">1.</span> Look at the bottom of the candidate's certificate — every Vidya Seva certificate has a verification panel with a QR and URL.</li>
                <li><span className="font-display text-[var(--vs-text)] font-semibold">2.</span> Scan the QR or paste the number above.</li>
                <li><span className="font-display text-[var(--vs-text)] font-semibold">3.</span> Receive an authoritative response from the trust's registry — including student name, type, reason, and issue date.</li>
              </ol>
              <div className="vs-divider my-5" />
              <div className="text-xs text-[var(--vs-text-secondary)] leading-relaxed">
                The trust does <strong>not</strong> share Aadhar, address, phone or guardian details on this public page. Only fields already printed on the certificate are confirmed.
              </div>
            </div>
          </motion.aside>
        </div>
      </main>

      <footer className="border-t border-[var(--vs-border)] bg-[var(--vs-bg-subtle)]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="text-xs text-[var(--vs-text-secondary)] underline underline-offset-4 hover:text-[var(--vs-secondary)]">← Back to landing</Link>
          <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--vs-secondary)]">
            <CertIcon size={14} weight="fill" /> Public registry · Anyone can verify
          </div>
        </div>
      </footer>
    </div>
  );
}
